import type { IUserJSON } from 'external-api-types';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'omit... Remove this comment to see the full error message
import omitDeep from 'omit-deep-lodash';
import Bugsnag from '@bugsnag/js';
import { getSubdomain } from 'tldts';
import logger from '../logging/logger';
import config from '../../../config';
import './segment';

interface AnalyticsUser {
    create_userId: IUserJSON['id'];
    email: IUserJSON['email'];
    create_createdAt: IUserJSON['updatedAt'];
    create_updatedAt: IUserJSON['createdAt'];
    create_projectTiers: IUserJSON['projectTiers'];
    create_organizationMemberships: IUserJSON['organizationMemberships'];
    create_connections: IUserJSON['connections'];
    create_preferences: IUserJSON['preferences'];
    __v: number;
    branch?: string;
    client: true;
}

function formatToAnalyticsUser(createUser: IUserJSON): AnalyticsUser {
    const user = omitDeep(createUser, 'accessToken', 'refreshToken', 'connectionUserId');

    return {
        create_userId: user.id,
        email: user.email,
        create_createdAt: user.createdAt,
        create_updatedAt: user.updatedAt,
        create_projectTiers: user.projectTiers,
        create_organizationMemberships: user.organizationMemberships,
        create_connections: user.connections,
        create_preferences: user.preferences,
        __v: user.__v,
        client: true,
        // Netlify AB Test Branch
        branch: process.env.BRANCH
    };
}

class Analytics {
    initialPageCalled: $TSFixMe;

    options: $TSFixMe;

    prevPath: $TSFixMe;

    constructor() {
        this.prevPath = null;
        this.initialPageCalled = false;
        this.options = {
            context: {
                screen: {
                    height: window.screen.height,
                    width: window.screen.width
                }
            }
        };

        if (config.logging.logentries.logAnalyticsEvents) {
            // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
            window.analytics.on('track', (evt: $TSFixMe, props: $TSFixMe, options: $TSFixMe) => {
                if (props && props.isError) {
                    logger.error(evt, { ...props, context: { ...options } });
                } else {
                    logger.info(evt, { ...props, context: { ...options } });
                }
            });
        }
        if (config.logging.logentries.logAnalyticsPageViews) {
            // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
            // eslint-disable-next-line max-params
            window.analytics.on('page', (evt: $TSFixMe, props: $TSFixMe, options: $TSFixMe, ctx: $TSFixMe) => {
                logger.info('page', { ...options, context: { ...ctx } });
            });
        }
    }

    onRouteChange(location: $TSFixMe) {
        const match = location.pathname.split('/');
        if (match && match[1] === 'edit') {
            this.page(false, { projectId: match[2] });
        }
        // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
        this.page(false);
    }

    page(onlyInitial: $TSFixMe, props: $TSFixMe) {
        if (onlyInitial && this.initialPageCalled) {
            return;
        }
        this.initialPageCalled = true;

        if (location.pathname === this.prevPath) {
            return;
        }

        // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
        window.analytics.page(
            {
                prevPath: this.prevPath
            },
            {
                context: {
                    ...props,
                    ...this.options.context,
                    traits: {
                        ...this.options.context.traits
                    }
                },
                ...this.getIntegrationsConfig()
            }
        );

        Bugsnag.leaveBreadcrumb(`Page: ${location.pathname}`, { metadata: props, type: 'navigation' });

        this.prevPath = location.pathname;
    }

    track(name: $TSFixMe, props: $TSFixMe) {
        return new Promise((resolve) => {
            const branch = process.env.BRANCH;
            const subdomain = getSubdomain(window.location.href);
            // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
            window.analytics.track(
                name,
                props,
                {
                    context: {
                        traits: {
                            ...(branch ? { branch } : {}),
                            ...(subdomain ? { subdomain } : null)
                        }
                    },
                    ...this.getIntegrationsConfig()
                },
                // @ts-expect-error TS(2794): Expected 1 arguments, but got 0. Did you forget to... Remove this comment to see the full error message
                () => resolve()
            );

            Bugsnag.leaveBreadcrumb(name, { metadata: props });
        });
    }

    identify({ userId, previousId, user }: { userId: string; previousId?: string; user: IUserJSON }) {
        const userTraits = formatToAnalyticsUser(user);

        const identifyUser = () => {
            // clear any cached traits so we aren't using stale data
            // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
            window.analytics.user()?.traits({});

            if (user.authProvider !== 'email' || user.emailVerification) {
                // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
                window.analytics.identify(userId, userTraits, { ...this.getIntegrationsConfig() });
            } else {
                // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
                window.analytics.identify(userTraits, { ...this.getIntegrationsConfig() });
            }
        };

        if (previousId) {
            // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
            window.analytics.alias(userId, previousId, undefined, identifyUser);
        } else {
            identifyUser();
        }

        Bugsnag.setUser(userId, user.email, user.email);

        // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
        logger.setDefaultProps({
            userId: userId,
            user: userTraits
        });

        // still needed for cio in app messaging working
        window._cio?.identify({
            id: userId,
            email: user.email
        });

        logger.info('user identified', { user: userTraits });
    }

    reset() {
        // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Windo... Remove this comment to see the full error message
        window.analytics.reset();
        // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
        logger.setDefaultProps({
            userId: null,
            user: null
        });
    }

    getIntegrationsConfig() {
        const sessionStart = this.getCookie('_sba_sess_start');
        if (!sessionStart) {
            return {};
        }

        return {
            integrations: {
                Amplitude: {
                    session_id: sessionStart
                }
            }
        };
    }

    getInitialReferrer() {
        return {
            initial_referrer: decodeURIComponent(this.getCookie('_sba_temp_ref') || 'unknown'),
            initial_referrer_landing: decodeURIComponent(this.getCookie('_sba_temp_refl') || ''),
            initial_traffic_source: decodeURIComponent(this.getCookie('_sba_temp_ts') || 'Unknown'),
            initial_utm_campaign: decodeURIComponent(this.getCookie('_sba_temp_utmc') || 'Unknown'),
            initial_utm_medium: decodeURIComponent(this.getCookie('_sba_temp_utmm') || 'Unknown'),
            initial_utm_source: decodeURIComponent(this.getCookie('_sba_temp_utms') || 'Unknown')
        };
    }

    getCookie(name: $TSFixMe) {
        const match = new RegExp(`(?:^|;)\\s*${name}\\s*=\\s*([^;]+)`).exec(document.cookie);
        if (match) {
            return match[1];
        }
        return null;
    }
}

export default new Analytics();
